import * as React from "react"

import { Skeleton, cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useFinanceFeatureQuery } from "@/features/finance/services/finance-query"
import { OverviewSurface } from "@/features/finance/model/finance-normalized-overview"
import { FinanceOverviewCardId, FinanceOverviewChartId } from "@/features/finance/model/finance-normalized-overview"
import {
  buildIncomeExpenseTrendRows,
  findChartById,
  findOverviewCard,
  readMoney,
} from "@/features/finance/lib/finance-overview-integration"
import type { FinanceOverviewQuery } from "@/features/finance/model/finance-types"
import type { DisplayAmount } from "@/shared/lib/formatting/currency"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { DashboardDivergingAreaChart } from "@/features/finance/overview/charts/dashboard-charts"
import { DashboardChartSkeleton } from "@/features/finance/overview/charts/chart-states"
import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import {
  useFinanceOverviewPeriod,
  useFinanceOverviewScope,
} from "@/features/finance/overview/context/finance-overview-context"
import { formatMoneySafe } from "@/features/finance/overview/lib/overview-format"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import { type FinanceSurfaceContext } from "@/features/finance/hooks/finance-context-rules"
import { WidgetShareNote } from "@/features/finance/overview/widgets/widget-share-note"
import {
  useWidgetCreateActions,
  type WidgetCreateKey,
} from "@/features/finance/overview/lib/use-widget-create-actions"

const WIDGET_CREATE_KEYS: readonly WidgetCreateKey[] = ["income", "expense"]

const INCOME_COLOR = "#10b981"
const EXPENSE_COLOR = "#f43f5e"

interface MoneyInOutData {
  currency: string
  /** All three are the SERVER's figures, as canonical decimal strings (IBA-116). Displayed, never computed. */
  income: DisplayAmount
  expense: DisplayAmount
  net: DisplayAmount
  points: Array<Record<string, string | number>>
}

/**
 * Money In & Out — compares money received vs money spent across the selected period. PERIOD-SENSITIVE:
 * its query key carries the period, so it refetches when the period changes. Internal transfers are excluded
 * server-side, so this is true organization income vs expense.
 */
export function MoneyInOutWidget({ context }: { context?: FinanceSurfaceContext } = {}) {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const period = useFinanceOverviewPeriod()
  const go = useOverviewNav()
  const { useFinanceOverviewQuery } = useFinanceFeatureQuery()

  const query = useFinanceOverviewQuery(
    scope.workspaceId && period.from && period.to
      ? ({
          workspaceId: scope.workspaceId,
          context: scope.overviewContext,
          branchId: scope.branchId ?? undefined,
          dateFrom: period.from,
          dateTo: period.to,
          profile: OverviewSurface.Dashboard,
        } satisfies FinanceOverviewQuery)
      : null,
  )

  const data = React.useMemo<MoneyInOutData | undefined>(() => {
    if (!query.data) return undefined
    const response = query.data
    const cards = response.cards ?? []
    // The net is the SERVER's — `income - expense` in the browser was a fallback that subtracted two floats and
    // could disagree, by a cent, with the very figure the backend had already computed from the ledger. The
    // backend always sends the netBalance card; when it somehow does not, zero is honest and a wrong total is not.
    const income = readMoney(findOverviewCard(cards, FinanceOverviewCardId.TotalIncome)?.value) ?? "0"
    const expense = readMoney(findOverviewCard(cards, FinanceOverviewCardId.TotalExpenses)?.value) ?? "0"
    const net = readMoney(findOverviewCard(cards, FinanceOverviewCardId.NetBalance)?.value) ?? "0"
    const chart = findChartById(response, FinanceOverviewChartId.IncomeExpenseTrend)
    const points = (chart ? buildIncomeExpenseTrendRows(chart) : [])
      .map((point): Record<string, string | number> => {
        const incomePoint = Math.max(0, Number(point["income"] ?? 0))
        const expensePoint = Math.max(0, Math.abs(Number(point["expense"] ?? 0)))
        return { ...point, income: incomePoint, expense: expensePoint === 0 ? 0 : -expensePoint }
      })
      .filter((point) => Number(point["income"] ?? 0) !== 0 || Number(point["expense"] ?? 0) !== 0)
    return {
      currency: response.meta?.currency ?? scope.currency,
      income,
      expense,
      net,
      points,
    }
  }, [query.data, scope.currency])

  const create = useWidgetCreateActions(WIDGET_CREATE_KEYS)
  useWidgetMenu({
    refresh: () => void query.refetch(),
    viewDetails: go.canNavigateTo(OverviewRoutes.transactions) ? () => go(OverviewRoutes.transactions) : undefined,
    create,
  })

  return (
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={data}
      isEmpty={(d) => d.points.length === 0}
      skeleton={<MoneyInOutSkeleton />}
      errorTitle={t("widgets.moneyInOut.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.moneyInOut.empty.title")}
    >
      {(d) => (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Metric label={t("widgets.moneyInOut.in")} value={formatMoneySafe(d.income, d.currency)} tone="text-emerald-600 dark:text-emerald-400" />
            <Metric label={t("widgets.moneyInOut.out")} value={formatMoneySafe(d.expense, d.currency)} tone="text-rose-600 dark:text-rose-400" />
            <Metric label={t("widgets.moneyInOut.net")} value={formatMoneySafe(d.net, d.currency)} tone="text-foreground" />
          </div>
          {/* Under the figures it describes — a caption above them reads as a heading. */}
          <WidgetShareNote context={context} />
          <DashboardDivergingAreaChart
            data={d.points}
            xKey="period"
            currency={d.currency}
            positiveSeries={{ key: "income", label: t("widgets.moneyInOut.in"), color: INCOME_COLOR }}
            negativeSeries={{ key: "expense", label: t("widgets.moneyInOut.out"), color: EXPENSE_COLOR }}
          />
        </div>
      )}
    </WidgetBoundary>
  )
}

function Metric({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div className="min-w-0">
      <p className="truncate text-[11px] text-muted-foreground">{label}</p>
      <p className={cn("truncate text-sm font-semibold tabular-nums", tone)}>{value}</p>
    </div>
  )
}

function MoneyInOutSkeleton() {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-2.5 w-12" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
      <DashboardChartSkeleton variant="area" />
    </div>
  )
}
